import { useEffect, useId, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import {
  ChevronRightIcon,
  FormCard,
  PrimaryButton,
  YesNoToggle,
} from './ui'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import ClaimsDesktopProgress from './ClaimsDesktopProgress'
import { formatClaimPolicySummaryLine, formatIncidentRailSummary } from '../claims/claimFlowModel'
import {
  persistWindscreenCompletedRepairerDetails,
  persistWindscreenReplacedAlready,
  persistWindscreenSmallChip,
  readClaimDraft,
} from '../claims/claimDraftStorage'
import {
  claimFormFieldClass,
  CLAIM_FORM_FIELD_ERROR_RING_WRAPPER_CLASS,
  ClaimFormFieldError,
  ClaimFormSubmitSummaryAlert,
  useClaimFormSubmitAttempt,
} from '../claims/claimFormValidationFeedback'
import { useScrollToTopOnErrorSignal } from '../hooks/useScrollToTopOnErrorSignal'

function buildWindscreenSearch(policyRef: string | null, typeInQuery: string | null): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (typeInQuery) p.set('type', typeInQuery)
  const q = p.toString()
  return q ? `?${q}` : ''
}

/**
 * Windscreen/glass branch after describe: small-chip question, replaced-or-repaired yes/no for all paths,
 * and who completed the repair when already done.
 */
export default function ClaimWindscreenDetailStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const chipHeadingId = useId()
  const replacedHeadingId = useId()
  const repairerHeadingId = useId()
  const companyFieldId = useId()

  const queryParams = new URLSearchParams(location.search)
  const policyRef = decodePolicyRef(queryParams.get('policyRef'))
  const typeInQuery = queryParams.get('type')
  const navState = location.state as { policy?: PolicyLink; preserveScroll?: boolean } | null

  const [chipAnswer, setChipAnswer] = useState<'Yes' | 'No' | undefined>(() => {
    const b = readClaimDraft()?.windscreenSmallChip
    if (b === true) return 'Yes'
    if (b === false) return 'No'
    return undefined
  })
  const [replacedAnswer, setReplacedAnswer] = useState<'Yes' | 'No' | null>(() => {
    const r = readClaimDraft()?.windscreenReplacedAlready
    if (r === true) return 'Yes'
    if (r === false) return 'No'
    return null
  })
  const [repairerCompany, setRepairerCompany] = useState(() => {
    const draft = readClaimDraft()
    if (draft?.windscreenReplacedAlready !== true) return ''
    return draft.windscreenRepairerCompany ?? ''
  })
  useEffect(() => {
    if (!navState?.preserveScroll) {
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const policySummary = formatClaimPolicySummaryLine(
    navState?.policy ?? (policyRef ? { policyRef } : null)
  )

  const windscreenSearch = buildWindscreenSearch(policyRef, typeInQuery)

  useEffect(() => {
    if (replacedAnswer !== 'Yes') return
    const t = window.setTimeout(() => {
      persistWindscreenCompletedRepairerDetails(policyRef, {
        repairerCompany,
        repairerEmail: '',
        repairerPhone: '',
      })
    }, 400)
    return () => window.clearTimeout(t)
  }, [policyRef, replacedAnswer, repairerCompany])

  const backToDescribe = () => {
    if (chipAnswer !== undefined) {
      persistWindscreenSmallChip(policyRef, chipAnswer === 'Yes')
    }
    persistWindscreenReplacedAlready(
      policyRef,
      replacedAnswer === 'Yes' ? true : replacedAnswer === 'No' ? false : null
    )
    if (replacedAnswer === 'Yes') {
      persistWindscreenCompletedRepairerDetails(policyRef, {
        repairerCompany,
        repairerEmail: '',
        repairerPhone: '',
      })
    }
    navigate(`/claims/windscreen${windscreenSearch}`, {
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }

  const chipOk = chipAnswer !== undefined
  const replacedOk = replacedAnswer !== null
  const showPriorRepairerFields = replacedAnswer === 'Yes'
  const priorRepairCompanyOk = !showPriorRepairerFields || repairerCompany.trim().length > 0
  const canProceed = chipOk && replacedOk && priorRepairCompanyOk

  const { submitAttempted, showSubmitSummary, attemptSubmit, clearSubmitAttempt } =
    useClaimFormSubmitAttempt(canProceed)
  const chipError = submitAttempted && !chipOk
  const replacedError = submitAttempted && !replacedOk
  const repairerCompanyError = submitAttempted && showPriorRepairerFields && !priorRepairCompanyOk

  const handleChipChange = (value: 'Yes' | 'No') => {
    clearSubmitAttempt()
    setChipAnswer(value)
    persistWindscreenSmallChip(policyRef, value === 'Yes')
    setReplacedAnswer(null)
    setRepairerCompany('')
    persistWindscreenReplacedAlready(policyRef, null)
  }

  const handleReplacedChange = (value: 'Yes' | 'No') => {
    clearSubmitAttempt()
    setReplacedAnswer(value)
    persistWindscreenReplacedAlready(policyRef, value === 'Yes')
    if (value === 'No') {
      setRepairerCompany('')
      return
    }
    setRepairerCompany('')
    persistWindscreenCompletedRepairerDetails(policyRef, {
      repairerCompany: '',
      repairerEmail: '',
      repairerPhone: '',
    })
  }

  const handleNext = () => {
    attemptSubmit(() => {
      if (chipAnswer === undefined || replacedAnswer === null) return
      persistWindscreenSmallChip(policyRef, chipAnswer === 'Yes')
      const p = new URLSearchParams()
      if (policyRef) p.set('policyRef', policyRef)
      if (typeInQuery) p.set('type', typeInQuery)
      const search = p.toString()
      const qs = search ? `?${search}` : ''

      if (replacedAnswer === 'Yes') {
        persistWindscreenReplacedAlready(policyRef, true)
        persistWindscreenCompletedRepairerDetails(policyRef, {
          repairerCompany,
          repairerEmail: '',
          repairerPhone: '',
        })
        navigate(`/claims/windscreen/review${qs}`, {
          state: navState?.policy ? { policy: navState.policy } : undefined,
        })
        return
      }

      persistWindscreenReplacedAlready(policyRef, false)
      const path = search ? `/claims/windscreen/repair-option?${search}` : '/claims/windscreen/repair-option'
      navigate(path, {
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
    })
  }

  const draftForRail = readClaimDraft()
  const repairerInputClass =
    'w-full rounded-xl border border-solid border-border-primary bg-background-primary px-[14px] py-[12px] font-body text-sm leading-sm text-text-primary-900 shadow-xs placeholder:text-text-placeholder focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid focus-visible:ring-offset-2'

  useScrollToTopOnErrorSignal(
    repairerCompanyError ? 'windscreen-detail-repairer-company-required' : null
  )

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader
        backAriaLabel="Back"
        onBack={backToDescribe}
        title="Add further detail"
      />

      <ClaimsFlowMain
        aside={
          <ClaimsDesktopProgress
            policySummary={policySummary}
            claimEntryBranch="windscreen"
            incidentSummary={formatIncidentRailSummary(draftForRail)}
            windscreenGstRegistered={draftForRail?.windscreenGstRegistered ?? null}
            windscreenAbn={draftForRail?.windscreenAbn ?? null}
            windscreenItcEntitled={draftForRail?.windscreenItcEntitled ?? null}
            windscreenItcPercentage={draftForRail?.windscreenItcPercentage ?? null}
            windscreenDescription={draftForRail?.windscreenDescription ?? ''}
            windscreenSmallChip={
              chipAnswer === 'Yes' ? true : chipAnswer === 'No' ? false : null
            }
            windscreenReplacedAlready={
              replacedAnswer === 'Yes' ? true : replacedAnswer === 'No' ? false : null
            }
            windscreenRepairOption={draftForRail?.windscreenRepairOption ?? null}
          />
        }
      >
        <FormCard>
          <section className="flex w-full flex-col items-start gap-[16px]" aria-labelledby={chipHeadingId}>
            {showSubmitSummary ? <ClaimFormSubmitSummaryAlert /> : null}
            <h2
              id={chipHeadingId}
              className="m-0 font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
            >
              Is the windscreen damage a small chip?
            </h2>
            <p className="m-0 font-body text-sm leading-sm text-text-primary-900">A small chip looks like:</p>
            <ul className="m-0 flex list-disc flex-col gap-[8px] pl-[20px] font-body text-sm leading-sm text-text-secondary-700 marker:text-text-secondary-700">
              <li className="pl-[4px]">smaller than a ten-cent coin</li>
              <li className="pl-[4px]">has no cracks spreading from it.</li>
            </ul>
            <fieldset
              className={`m-0 w-full min-w-0 border-0 p-0 ${chipError ? CLAIM_FORM_FIELD_ERROR_RING_WRAPPER_CLASS : ''}`}
            >
              <legend className="sr-only">Is the windscreen damage a small chip? Choose yes or no.</legend>
              <YesNoToggle value={chipAnswer ?? undefined} onChange={handleChipChange} />
            </fieldset>
            {chipError ? (
              <ClaimFormFieldError id={`${chipHeadingId}-error`}>Choose Yes or No to continue.</ClaimFormFieldError>
            ) : null}

            <div className="flex w-full flex-col gap-[12px] border-t border-border-secondary pt-[16px]">
              <h2
                id={replacedHeadingId}
                className="m-0 font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
              >
                Has the windscreen already been repaired or replaced?
              </h2>
              <fieldset
                className={`m-0 w-full min-w-0 border-0 p-0 ${replacedError ? CLAIM_FORM_FIELD_ERROR_RING_WRAPPER_CLASS : ''}`}
              >
                <legend className="sr-only">
                  Has the windscreen already been repaired or replaced? Choose yes or no.
                </legend>
                <YesNoToggle value={replacedAnswer ?? undefined} onChange={handleReplacedChange} />
              </fieldset>
              {replacedError ? (
                <ClaimFormFieldError id={`${replacedHeadingId}-error`}>
                  Choose Yes or No to continue.
                </ClaimFormFieldError>
              ) : null}

              {showPriorRepairerFields ? (
                <div className="flex w-full flex-col gap-[16px] border-t border-border-secondary pt-[16px]">
                  <div>
                    <h2
                      id={repairerHeadingId}
                      className="m-0 font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
                    >
                      Who completed the repair?
                    </h2>
                    <p className="mt-[8px] m-0 font-body text-sm leading-sm text-text-tertiary-600">
                      Send us a copy of your paid invoice to claims@club4x4.com.au along with your BSB, Account Number, and Account Name for reimbursement.
                    </p>
                  </div>
                  <div className="flex flex-col gap-[8px]">
                    <label htmlFor={companyFieldId} className="font-body text-sm font-semibold text-text-primary-900">
                      Company or business name
                    </label>
                    <input
                      id={companyFieldId}
                      type="text"
                      value={repairerCompany}
                      onChange={(e) => {
                        clearSubmitAttempt()
                        setRepairerCompany(e.target.value)
                      }}
                      autoComplete="organization"
                      aria-invalid={repairerCompanyError}
                      aria-describedby={repairerCompanyError ? `${companyFieldId}-error` : undefined}
                      className={claimFormFieldClass(repairerInputClass, repairerCompanyError)}
                    />
                    {repairerCompanyError ? (
                      <ClaimFormFieldError id={`${companyFieldId}-error`}>
                        Enter the company or business name to continue.
                      </ClaimFormFieldError>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        </FormCard>

        <div className="sticky bottom-0 w-full border-t border-border-secondary bg-background-secondary pt-[12px] pb-[8px] md:static md:border-t-0 md:py-0">
          <PrimaryButton
            type="button"
            size="lg"
            fullWidth
            onClick={handleNext}
            rightIcon={
              <div className="size-5 text-text-white">
                <ChevronRightIcon />
              </div>
            }
            aria-label="Next"
          >
            Next
          </PrimaryButton>
        </div>
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}
